import React, { useState, useEffect, useRef, useCallback } from "react";
import { listUsers } from "../../api/adminUsersApi";
import { PASS_STATUS, createPass } from "../../api/studentPassesApi";
import { showSuccess, showError } from "../../utils/notifications";
import { getContextualError, logError } from "../../utils/errorHandling";
import type { components } from "../../api/types.gen";
import styles from "./TeacherPassSubmission.module.css";

type UserResponse = components["schemas"]["UserResponse"];

// The destination fields this modal renders. A full DestinationResponse (as
// passed by TeacherDashboard) structurally satisfies this.
export interface Destination {
  id: number;
  name: string;
}

interface TeacherPassSubmissionProps {
  isOpen: boolean;
  onClose: () => void;
  destinations: Destination[];
  destinationsError?: string | null;
  onRetryDestinations?: () => void;
}

const TeacherPassSubmission = ({
  isOpen,
  onClose,
  destinations,
  destinationsError = null,
  onRetryDestinations,
}: TeacherPassSubmissionProps): React.ReactElement | null => {
  const [studentSearch, setStudentSearch] = useState("");
  const [selectedStudent, setSelectedStudent] = useState<UserResponse | null>(
    null,
  );
  const [selectedDestination, setSelectedDestination] = useState("");
  const [searchResults, setSearchResults] = useState<UserResponse[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [note, setNote] = useState("");
  const latestSearchRef = useRef("");

  // Search for students when input changes
  useEffect(() => {
    latestSearchRef.current = studentSearch;

    const searchStudents = async () => {
      if (studentSearch.length < 2) {
        setSearchResults([]);
        // Reset here too: if the input is shortened below 2 chars while a
        // previous search left isSearching on, this early return would
        // otherwise leave "Searching..." stuck on permanently.
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      try {
        const { data } = await listUsers({
          q: studentSearch,
          role: "STUDENT",
          limit: 10,
        });
        if (latestSearchRef.current === studentSearch) {
          setSearchResults(data);
        }
      } catch (error) {
        logError(error, "searchStudents");
        if (latestSearchRef.current === studentSearch) {
          setSearchResults([]);
        }
      }
      if (latestSearchRef.current === studentSearch) {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(searchStudents, 300);
    return () => clearTimeout(debounceTimer);
  }, [studentSearch]);

  const handleSelectStudent = (student: UserResponse) => {
    setSelectedStudent(student);
    setStudentSearch(student.name || student.email);
    setSearchResults([]);
  };

  const handleResultKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    student: UserResponse,
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSelectStudent(student);
    }
  };

  const handleSubmit = async () => {
    if (!selectedStudent || !selectedDestination) {
      showError("Please select a student and destination");
      return;
    }

    setIsSubmitting(true);
    try {
      const pass = await createPass({
        studentId: selectedStudent.id,
        destinationId: Number(selectedDestination),
        note: note.trim() || undefined,
      });

      showSuccess(
        pass.status === PASS_STATUS.WAITING
          ? `${selectedStudent.name || selectedStudent.email} added to the waiting list (destination full)`
          : `Pass created for ${selectedStudent.name || selectedStudent.email}`,
      );

      handleClose();
    } catch (error) {
      const err = error as Error & { status?: number };
      if (err.status === 422 || err.status === 409) {
        showError(err.message);
      } else {
        logError(error, "teacherCreatePass");
        showError(getContextualError(error, "pass-request"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = useCallback(() => {
    setStudentSearch("");
    setSelectedStudent(null);
    setSelectedDestination("");
    setSearchResults([]);
    // Reset the in-flight indicator so a "Searching..." left on at close time
    // doesn't persist across a subsequent reopen of the modal.
    setIsSearching(false);
    setNote("");
    onClose();
  }, [onClose]);

  // Close on Escape while the modal is open, matching the overlay-click and
  // Cancel affordances so keyboard users get the same dismissal path.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, handleClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleClose();
  };

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (!isOpen) return null;

  return (
    <div className={styles["teacher-pass-submission-modal"]}>
      <div
        className={styles["teacher-pass-modal-overlay"]}
        onClick={handleOverlayClick}
      />
      <div className={styles["modal-content"]} onClick={handleContentClick}>
        <div className={styles["modal-header"]}>
          <h2>Create Pass for Student</h2>
          <button
            type="button"
            className={styles["close-button"]}
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className={styles["modal-body"]}>
          {/* Student Search */}
          <div className={styles["form-group"]}>
            <label htmlFor="teacher-pass-student-search">Student Name</label>
            <div className={styles["search-container"]}>
              <input
                id="teacher-pass-student-search"
                type="text"
                value={studentSearch}
                onChange={(e) => {
                  setStudentSearch(e.target.value);
                  setSelectedStudent(null);
                }}
                placeholder="Start typing student name..."
                className={styles["student-search-input"]}
              />
              {isSearching && (
                <span className={styles["searching-indicator"]}>
                  Searching...
                </span>
              )}

              {searchResults.length > 0 && (
                <div className={styles["search-results"]}>
                  {searchResults.map((student) => (
                    <div
                      key={student.id}
                      className={styles["search-result-item"]}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelectStudent(student)}
                      onKeyDown={(e) => handleResultKeyDown(e, student)}
                    >
                      <div className={styles["student-name"]}>
                        {student.name || student.email}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selectedStudent && (
              <div className={styles["selected-student"]}>
                ✓ Selected: {selectedStudent.name || selectedStudent.email}
              </div>
            )}
          </div>

          {/* Destination Selection */}
          <div className={styles["form-group"]}>
            <label htmlFor="teacher-pass-destination">Destination</label>
            <select
              id="teacher-pass-destination"
              value={selectedDestination}
              onChange={(e) => setSelectedDestination(e.target.value)}
              className={styles["destination-select"]}
            >
              <option value="">Select destination...</option>
              {destinations.map((dest) => (
                <option key={dest.id} value={dest.id}>
                  {dest.name}
                </option>
              ))}
            </select>
            {/* Mirrors StudentDashboard's destinations-error + Retry so a
                failed destinations fetch doesn't leave this modal silently
                unusable (empty dropdown, permanently disabled submit). */}
            {destinationsError && (
              <div className="destinations-error">
                <small>🚫 {destinationsError}</small>{" "}
                {onRetryDestinations && (
                  <button type="button" onClick={() => onRetryDestinations()}>
                    Retry
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Optional Note */}
          <div className={styles["form-group"]}>
            <label htmlFor="teacher-pass-note">Note (Optional)</label>
            <textarea
              id="teacher-pass-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add any additional notes..."
              className={styles["note-textarea"]}
              rows={3}
            />
          </div>
        </div>

        <div className={styles["modal-footer"]}>
          <button
            className={styles["btn-secondary"]}
            onClick={handleClose}
            disabled={false}
          >
            Cancel
          </button>
          <button
            className={`${styles["btn-primary"]} submit-btn`}
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedStudent || !selectedDestination}
          >
            {isSubmitting ? "Submitting..." : "Submit Pass"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeacherPassSubmission;
